import React, { useState, useEffect, useRef } from "react";
import { FaVideo, FaStethoscope, FaInfoCircle } from "react-icons/fa";

import { useTranslation } from "react-i18next";
import { getDepartments, getDoctors, getDepartmentAvailableSlots, getDoctorFees, checkDoctorWalkins, createSlotsWebSocket } from "../Services/api";
import AvailableSlotsPopup from "./AvailableSlotsPopup";
import { toast } from "react-toastify";
import Select from "react-select";
import Loader from './Loader';
import TelemedicineInfoPopup from "./TelemedicineInfoPopup";

const reactSelectStyles = (isDark, isReadOnly) => ({
  control: (base, state) => ({
    ...base,
    backgroundColor: isReadOnly
      ? isDark
        ? "#374151" // match dark:bg-gray-700 for readonly
        : "#f9fafb" // match bg-gray-50 for readonly
      : isDark
        ? "#1f2937" // normal dark mode editable
        : "#ffffff", // normal light mode editable
    cursor: isReadOnly ? "not-allowed" : "pointer",
    borderColor: state.isFocused
      ? "#3b82f6"
      : isDark
        ? "#4b5563"
        : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
  }),

  menu: (base) => ({
    ...base,
    backgroundColor: isDark ? "#1f2937" : "#ffffff",
    zIndex: 9999,
  }),

  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),

  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? isDark
        ? "#374151"
        : "#e5e7eb"
      : state.isSelected
        ? "#2563eb"
        : isDark
          ? "#1f2937"
          : "#ffffff",
    color: state.isSelected ? "#ffffff" : isDark ? "#f9fafb" : "#111827",
    cursor: "pointer",
  }),

  singleValue: (base) => ({
    ...base,
    color: isReadOnly
      ? isDark
        ? "#9ca3af" // dimmed text for readonly dark
        : "#6b7280" // dimmed text for readonly light
      : isDark
        ? "#f9fafb"
        : "#111827",
  }),

  input: (base) => ({
    ...base,
    color: isReadOnly
      ? isDark
        ? "#9ca3af"
        : "#6b7280"
      : isDark
        ? "#f9fafb"
        : "#111827",
  }),

  placeholder: (base) => ({
    ...base,
    color: isDark ? "#9ca3af" : "#6b7280",
  }),
});



export default function DoctorDetails({ form, onChange, isExistingPatient }) {
  const { t } = useTranslation();
  const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches
    || document.documentElement.classList.contains("dark");
  const wsRef = useRef(null);
  const [showTelemedicine, setShowTelemedicine] = useState(false);
  const [showTeleInfo, setShowTeleInfo] = useState(false);
  const [visitType, setVisitType] = useState("W"); // "W" = Walk-in, "A" = Appointment

  const isAppointmentEnabled = process.env.REACT_APP_IS_APPOINTMENT_ENABLED === "true";
  const isWalkinEnabled = process.env.REACT_APP_IS_WALKIN_ENABLED === "true";

  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [deptError, setDeptError] = useState(null);

  const [doctors, setDoctors] = useState([]);

  const [doctorError, setDoctorError] = useState(null);
  const [showNotes, setShowNotes] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState(false);
  const [slotConflictError, setSlotConflictError] = useState(null);

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState("");
  const [openSlots, setOpenSlots] = useState(false);
  const fee_type = isExistingPatient ? "genreg" : "genreg";
  const [doctorFee, setDoctorFee] = useState(null);
  const payGatewayEnabled = process.env.REACT_APP_PAY_GATEWAY_ENABLE === "true";
  const payMandatory = process.env.REACT_APP_PAY_MANDATORY === "true";
  const payAtCounterEnabled = process.env.REACT_APP_PAY_AT_COUNTER_ENABLED === "true";
  const showPayNow = payGatewayEnabled;
  const showPayCounter = payAtCounterEnabled;
  const disablePayCounter = payMandatory;
  const forcePayNow = payMandatory && payGatewayEnabled;
  const invalidConfig = payMandatory && !payGatewayEnabled;
  const onlyPayCounterAvailable = showPayCounter && !showPayNow;
  const [isFreeConsultation, setIsFreeConsultation] = useState(false);
  const handleSlotSelect = (slot) => {
    if (!slot?.available) {
      toast.error(t("slotErrors.noLongerAvailable"));
      return;
    }

    onChange({
      target: { name: "slotDateTime", value: slot }
    });
    setSlotConflictError(null);

    setOpenSlots(false); // optionally close popup after selection
  };
  const VISIT_TYPE = {
    WALK_IN: "W",
    APPOINTMENT: "A"
  };
  const handleWalkIn = async () => {
    try {
      setLoadingMessage("Fetching Doctors...");
      setLoading(true);
      setVisitType(VISIT_TYPE.WALK_IN);

      await getDoctors(
        VISIT_TYPE.WALK_IN,
        null,
        form.appointmentDate
      );

    } catch (err) {
      console.error("Error fetching doctors:", err.response || err);
      setDoctorError("Failed to load doctors");
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAppointment = async () => {
    try {
      setLoadingMessage("Fetching Doctors...");
      setLoading(true);
      setVisitType(VISIT_TYPE.APPOINTMENT);

      await getDoctors(
        VISIT_TYPE.APPOINTMENT,
        null,
        form.appointmentDate
      );

    } catch (err) {
      console.error("Error fetching doctors:", err.response || err);
      setDoctorError("Failed to load doctors");
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = async (e) => {
    const selectedDate = e.target.value;

    setAppointmentDate(selectedDate);
    onChange({
      target: { name: "appointmentDate", value: selectedDate }
    });

    // Only refetch for Appointment
    if (form.visitType === "appointment") {
      try {
        setLoadingMessage("Fetching Doctors...");
        setLoading(true);

        const res = await getDoctors(
          VISIT_TYPE.APPOINTMENT,
          form.selectedDepartment ? form.selectedDepartment : null, // pass null if not selected
          selectedDate
        );
         setDoctors((res?.data || []).map((doc) => ({
          doctorId: String(doc.doctorId || doc.id),
          name: doc.name,
        })));
        setDoctorError(null);
      } catch (err) {
        console.error("Error fetching doctors:", err.response || err);
        setDoctorError("Failed to load doctors");
        setDoctors([]);
      } finally {
        setLoading(false);
      }
    }
  };


  useEffect(() => {
    if (!selectedDoctorId || !appointmentDate) return;

    if (!wsRef.current) {
      wsRef.current = createSlotsWebSocket((data) => {
        console.log("📩 WS update:", data);
        const myPatientId = localStorage.getItem("patientId");


        // Always update UI
        setSlots((prev) =>
          prev.map((slot) =>
            slot.slotId === data.slotId ? { ...slot, available: false } : slot
          )
        );

        // Ignore own booking
        if (String(data.patientId) === String(myPatientId)) {
          console.log("🟢 Own booking event — ignoring");
          return;
        }

        // Only others reach here
        if (form.slotDateTime?.slotId === data.slotId) {
          setSlotConflictError(
            t("slotErrors.justBooked")
          );
          onChange({ target: { name: "slotDateTime", value: null } });
        }
      });

    }

    return () => {
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [selectedDoctorId, appointmentDate, form.slotDateTime]);


  useEffect(() => {
    setIsFreeConsultation(doctorFee === 0);
  }, [doctorFee]);
  const isDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches
    || document.documentElement.classList.contains("dark");
  useEffect(() => {
    if (invalidConfig) {
      console.warn("Payment mandatory but gateway disabled. Check env config.");
    }
  }, [invalidConfig]);

  const [paymentMode, setPaymentMode] = useState("pay_now");

  const todayDate = new Date();
  const day = String(todayDate.getDate()).padStart(2, '0');
  const month = String(todayDate.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const year = todayDate.getFullYear();
  const today = `${year}-${month}-${day}`;

  const [anyDepartment, setAnyDepartment] = useState(false);
  const [isWalkinsAllowed, setiIWalkinsAllowed] = useState(false);
  const handleChange = (mode) => {
    onChange({ target: { name: "paymentMode", value: mode } });
  };
  //   useEffect(() => {
  //     if (forcePayNow && form.paymentMode !== "pay_now") {
  //       handleChange("pay_now");
  //     }
  //   }, [forcePayNow, form.paymentMode]);
  //   useEffect(() => {
  //     if (onlyPayCounterAvailable && form.paymentMode !== "pay_counter") {
  //       handleChange("pay_counter");
  //     }
  //   }, [onlyPayCounterAvailable, form.paymentMode]);
  //  useEffect(() => {
  //     if (doctorFee === 0) {
  //       if (form.paymentMode !== null) {
  //         onChange({ target: { name: "paymentMode", value: null } });
  //       }
  //     }
  //   }, [doctorFee, form.paymentMode]);
  //   useEffect(() => {
  //     if (doctorFee > 0 && !form.paymentMode) {
  //       if (forcePayNow) {
  //         onChange({ target: { name: "paymentMode", value: "pay_now" } });
  //       } else if (onlyPayCounterAvailable) {
  //         onChange({ target: { name: "paymentMode", value: "pay_counter" } });
  //       }
  //     }
  //   }, [doctorFee, form.paymentMode, forcePayNow, onlyPayCounterAvailable]);

  useEffect(() => {
    if (form.visitType === "appointment" && !form.appointmentType) {
      onChange({
        target: { name: "appointmentType", value: "in_person" }
      });
    }
  }, [form.visitType]);


  useEffect(() => {
    if (form.specialNotes && form.specialNotes.trim() !== "") {
      setShowNotes(true);
    }
  }, [form.specialNotes]);
  useEffect(() => {
    let nextMode = form.paymentMode;
    console.log("FEE", doctorFee);

    // Free consultation → no payment
    if (doctorFee === 0) {
      nextMode = null;
    } else {
      // Paid consultation → apply rules
      if (forcePayNow) nextMode = "pay_now";
      else if (onlyPayCounterAvailable) nextMode = "pay_counter";
    }

    if (nextMode !== form.paymentMode) {
      onChange({ target: { name: "paymentMode", value: nextMode } });
    }

  }, [doctorFee, forcePayNow, onlyPayCounterAvailable, form.paymentMode]);

  useEffect(() => {
    if (isWalkinEnabled === undefined || isAppointmentEnabled === undefined) return;
    if (isWalkinEnabled) {
      onChange({ target: { name: "visitType", value: "walkin" } });

      setAppointmentDate(today);
      onChange({ target: { name: "appointmentDate", value: today } });
      onChange({ target: { name: "slotDateTime", value: null } });

    } else if (isAppointmentEnabled) {
      onChange({ target: { name: "visitType", value: "appointment" } });

      setAppointmentDate(today);
      onChange({ target: { name: "appointmentDate", value: today } });

    } else {
      onChange({ target: { name: "visitType", value: "" } });
    }
  }, [isWalkinEnabled, isAppointmentEnabled]);




  useEffect(() => {
    if (!form.paymentMode) {
      onChange({ target: { name: "paymentMode", value: "pay_now" } });
    }
  }, []);
  useEffect(() => {
    if (form.consultationFee == null) {
      setDoctorFee(null);
    }
  }, [form.consultationFee]);



  useEffect(() => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const year = today.getFullYear();
    const formattedDate = `${year}-${month}-${day}`;
    setAppointmentDate(formattedDate);
  }, []);



  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      setLoadingDepartments(true);
      try {
        const res = await getDepartments();
        setDepartments(res?.data || []);
        setDeptError(null);
      } catch (err) {
        console.error("Error fetching departments:", err.response || err);
        setDeptError("Failed to load departments");
      } finally {
        setLoadingDepartments(false);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch doctors when department changes
  useEffect(() => {
    if (anyDepartment) return; // skip if checkbox is ON
    if (!form.selectedDepartment) return;
    setLoadingMessage("Fetching Doctors...");
    setLoading(true);

    const fetchDoctors = async () => {
      try {
        let type = VISIT_TYPE.WALK_IN;

        if (form.visitType === "appointment") {
          type = VISIT_TYPE.APPOINTMENT;
        }
        const res = await getDoctors(type, form.selectedDepartment, form.appointmentDate);
        setDoctors((res?.data || []).map((doc) => ({
          doctorId: String(doc.doctorId || doc.id),
          name: doc.name,
        })));
        setDoctorError(null);
      } catch (err) {
        console.error("Error fetching doctors:", err.response || err);
        setDoctorError("Failed to load doctors");
        setDoctors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctors();
  }, [form.selectedDepartment]);

  const handleFetchSlots = async () => {
    if (!form.selectedDoctor) {
      toast.warn("Please select a doctor first");
      return;
    }
    try {
      setLoadingSlots(true);
      const allSlots = await getDepartmentAvailableSlots(form.selectedDoctor, appointmentDate, fee_type);
      const doctorObj = allSlots.find(doc => String(doc.doctorId) === String(form.selectedDoctor));
      setSlots(doctorObj?.availableSlots || []);
    } catch (err) {
      console.error("Error fetching slots:", err);
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };
  const [formData, setFormData] = useState({
    visitType: null,
    selectedDoctor: null,
    slotDateTime: null,
    paymentMode: null
  });
  const stepConfig = {
    walkin: [
      t("doctor.steps.visit"),
      t("doctor.steps.doctor"),
      t("doctor.steps.payment"),
      t("doctor.steps.confirm")
    ],
    appointment: [
      t("doctor.steps.visit"),
      t("doctor.steps.doctor"),
      t("doctor.steps.slot"),
      t("doctor.steps.payment"),
      t("doctor.steps.confirm")
    ]
  };

  const steps = stepConfig[form.visitType] || [];



  const getCurrentStep = (form) => {
    if (!form.visitType) return 0;              // Visit
    if (!form.selectedDoctor) return 1;         // Doctor

    if (form.visitType === "appointment") {
      if (!form.slotDateTime) return 2;         // Slot
      if (!form.paymentMode) return 3;          // Payment
      return 4;                                 // Confirm
    } else {
      if (!form.paymentMode) return 2;          // Payment
      return 3;                                 // Confirm
    }
  };

  const currentStep = getCurrentStep(form);

  useEffect(() => {
    setFormData(f => ({ ...f, slotDateTime: null }));
  }, [form.selectedDoctor]);

  useEffect(() => {
    setFormData(f => ({ ...f, slotDateTime: null, paymentMode: null }));
  }, [form.visitType]);
  // derive and apply paymentMode deterministically
  useEffect(() => {
    // canonical inputs
    const doctorSelected = !!form.selectedDoctor;
    const fee = doctorFee; // can be null, 0, or >0

    // Treat doctorFee === 0 as "free consultation"
    const isFree = fee === 0;

    // Compute desired paymentMode from scratch (single source of truth)
    let desired = null;

    if (isFree) {
      // free -> disable payments
      desired = null;
    } else if (!doctorSelected) {
      // doctor cleared -> restore initial / default rules (like on load)
      // Priority: forced online -> only-counter -> prefer online if available -> prefer counter if available -> null
      if (forcePayNow) desired = "pay_now";
      else if (onlyPayCounterAvailable) desired = "pay_counter";
      else if (showPayNow) desired = "pay_now";
      else if (showPayCounter) desired = "pay_counter";
      else desired = null;
    } else {
      // doctor selected and not free (fee > 0)
      if (forcePayNow) desired = "pay_now";
      else if (onlyPayCounterAvailable) desired = "pay_counter";
      else {
        // preserve existing selection if user already picked one, otherwise prefer pay_now if available
        desired = form.paymentMode || (showPayNow ? "pay_now" : showPayCounter ? "pay_counter" : null);
      }
    }

    // update isFreeConsultation state (if you keep it)
    setIsFreeConsultation(isFree);

    // only call parent onChange when actual value differs (avoid loops)
    if (desired !== form.paymentMode) {
      onChange({ target: { name: "paymentMode", value: desired } });
    }
  }, [
    form.selectedDoctor,
    doctorFee,
    forcePayNow,
    onlyPayCounterAvailable,
    showPayNow,
    showPayCounter,
    // don't include form.paymentMode in deps to avoid timing confusion; we compare before calling onChange
  ]);



  const handleDoctorChange = async (e) => {
    console.log("SEL DOC", form.selectedDoctor);

    const selectedDoctorId = e.target.value;
    setSelectedDoctorId(selectedDoctorId);
    setDoctorFee(null);
    onChange({ target: { name: "slotDateTime", value: null } });
    onChange({ target: { name: "doctorFee", value: "" } });
    onChange({ target: { name: "consultationFee", value: "" } });
    const selectedDoctor = doctors.find(doc => doc.doctorId === selectedDoctorId);

    // update doctor selection
    onChange(e);

    if (!selectedDoctorId) return;


    // set doctor name
    onChange({
      target: {
        name: "selectedDoctorName",
        value: selectedDoctor ? selectedDoctor.name : ""
      }
    });

    // 🔥 fetch doctor fee
    try {
      setLoadingMessage("Fetching Doctor Fees...");
      setLoading(true);
      const rawPatientId = localStorage.getItem("patientId");

      const patientIdInt =
        rawPatientId && rawPatientId !== "null" && rawPatientId !== "undefined"
          ? rawPatientId
          : "";

      console.log("ID", patientIdInt);


      const feeResponse = await getDoctorFees(selectedDoctorId, fee_type, patientIdInt);

      if (feeResponse) {
        setDoctorFee(feeResponse.consultationFee);
        setShowTelemedicine(feeResponse.telemedicineEnabled);
        // also store inside form if needed
        onChange({
          target: {
            name: "consultationFee",
            value: feeResponse.consultationFee
          }
        });
        if (form.visitType === "appointment" && selectedDoctorId) {
          setLoadingMessage("Fetching Available Slots...");
          const allSlots = await getDepartmentAvailableSlots(selectedDoctorId, appointmentDate, fee_type);
          const doctorObj = allSlots.find(doc => String(doc.doctorId) === String(selectedDoctorId));
          setSlots(doctorObj?.availableSlots || []);
          setOpenSlots(true); // open popup
        }
      }

    } catch (err) {
      console.error("Error fetching doctor fee", err);
      setDoctorFee(null);
      setSlots([]);
    } finally {
      setLoading(false);
    }


    // ************************************
    // 🔥 2. Call checkDoctorWalkins API
    // ************************************

    // ************************************
    // 🔥 2. Call checkDoctorWalkins API (WALK-IN ONLY)
    // ************************************
    if (form.visitType === "walkin") {
      try {
        setLoadingMessage("Checking Walk-in Availability...");
        setLoading(true);

        const walkinsResponse = await checkDoctorWalkins(
          selectedDoctorId,
          appointmentDate // YYYY-MM-DD
        );

        if (walkinsResponse) {
          const available = walkinsResponse.availableWalkins;

          // update form state
          onChange({
            target: {
              name: "availableWalkins",
              value: available
            }
          });

          // ❌ If no slots available → show error + disable submit
          if (available <= 0) {
            toast.error("No walk-in slots available for this doctor on the selected date.");
            setiIWalkinsAllowed(false);
          }
          else {
            // enable submit button
            setiIWalkinsAllowed(true);
          }
        }
      } catch (err) {
        console.error("Error checking walk-ins", err);
        toast.error("Failed to check walk-in availability.");
      } finally {
        setLoading(false);
      }
    }
  }
  useEffect(() => {
    if (form.visitType === "walkin" && form.appointmentType !== "in_person") {
      onChange({
        target: { name: "appointmentType", value: "in_person" }
      });
    }
  }, [form.visitType]);


  return (
    <div className="relative">
      {/* Show loader on top of the form */}
      {loading && <Loader message={loadingMessage} />}
      <div className="bg-blue-100 dark:bg-blue-600/30 rounded-2xl shadow-md p-6 mb-6 transition-colors duration-300 border border-blue-200 dark:border-blue-700 shadow-md" >
        <h3 className="text-xl font-semibold text-sky-700 dark:text-sky-300
                   border-b-2 border-sky-400 mb-4">
          {t("doctor.title")}
        </h3>
        <div className="flex items-center mb-6 w-full">

          {steps.length > 0 && steps.map((step, idx) => {
            const isActive = idx === currentStep;
            const isCompleted = idx < currentStep;

            return (
              <div key={idx} className="flex items-center w-full">

                {/* Step */}
                <div className="flex flex-col sm:flex-row items-center shrink-0">

                  <div
                    className={`w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full text-[10px] sm:text-sm font-bold transition-all
            ${isCompleted
                        ? "bg-green-600 text-white"
                        : isActive
                          ? "bg-blue-600 text-white"
                          : "bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200"}`}
                  >
                    {isCompleted ? "✓" : idx + 1}
                  </div>

                  <span
                    className={`mt-1 sm:mt-0 sm:ml-2 text-[10px] sm:text-sm text-center transition-all leading-tight whitespace-nowrap
            ${isCompleted
                        ? "text-green-600 font-semibold"
                        : isActive
                          ? "text-blue-600 font-semibold"
                          : "text-gray-500 dark:text-gray-400"}`}
                  >
                    {step}
                  </span>

                </div>

                {/* Connector line */}
                {idx < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 sm:mx-4 transition-all
            ${isCompleted ? "bg-green-600" : "bg-gray-300 dark:bg-gray-600"}`}
                  />
                )}

              </div>
            );
          })}

        </div>




        {/* ============================
    VISIT TYPE (iOS Style Buttons)
=============================== */}
        <div className="mt-6 w-full md:w-1/2">
          {/* <label className="label font-semibold">{t("visit.visitType")}</label> */}

          <div className="flex bg-gray-200 dark:bg-gray-700 rounded-2xl p-1 mt-3
              shadow-inner border border-gray-300 dark:border-gray-600">

            {/* Walk-in */}
            {isWalkinEnabled && (
              <button
                type="button"
                className={`flex-1 px-3 py-3 rounded-xl text-center 
            text-[11px] sm:text-sm font-medium leading-tight
            break-words whitespace-normal
            transition-all duration-200
            ${form.visitType === "walkin"
                    ? "bg-[#0A84FF] text-white shadow-md"
                    : "text-gray-700 dark:text-gray-300"
                  }`}
                onClick={() => {
                  onChange({ target: { name: "visitType", value: "walkin" } });

                  // Set today
                  setAppointmentDate(today);
                  onChange({
                    target: { name: "appointmentDate", value: today }
                  });

                  // Clear previous slot
                  onChange({
                    target: { name: "slotDateTime", value: null }
                  });
                  handleWalkIn();
                }}
              >
                {t("visit.walkin")}
              </button>
            )}
            {isAppointmentEnabled && (
              <button
                type="button"
                className={`flex-1 px-3 py-3 rounded-xl text-center 
            text-[11px] sm:text-sm font-medium leading-tight
            break-words whitespace-normal
            transition-all duration-200
            ${form.visitType === "appointment"
                    ? "bg-[#0A84FF] text-white shadow-md"
                    : "text-gray-700 dark:text-gray-300"
                  }`}

                onClick={() => {
                  onChange({ target: { name: "visitType", value: "appointment" } });

                  setAppointmentDate(today);
                  onChange({
                    target: { name: "appointmentDate", value: today }
                  });
                  handleAppointment();
                }}
              >
                {t("visit.appointment")}
              </button>
            )}


          </div>
        </div>


        {/* ============================
    APPOINTMENT DATE
=============================== */}
        <div className="mt-6 w-full md:w-1/2">
          <label className="label text-gray-700 dark:text-gray-200">
            {t("visit.selectDate")} <span className="text-red-500">*</span>
          </label>

          <div
            className="bg-white dark:bg-gray-700 
      border border-gray-300 dark:border-gray-600 
      rounded-md px-3 py-3 mt-2 shadow-sm flex items-center"
          >
            <input
              type="date"
              min={form.visitType === "appointment" ? today : today}
              disabled={form.visitType === "walkin"}      // Disable for walk-in
              className={`w-full bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none
    ${form.visitType === "walkin" ? "opacity-60 cursor-not-allowed" : ""}
  `}
              value={appointmentDate}
              onChange={handleDateChange}
            />

          </div>
        </div>

        {/* ============================
    DEPARTMENT (FULL WIDTH MATCH)
=============================== */}
        <div className="mt-6 w-full md:w-1/2">
          <div className="flex items-center justify-between w-full mb-2">
            {/* Left: Department Label */}

            <label className="label text-gray-700 dark:text-gray-200">
              {t("doctor.department")}
            </label>
            {/* Right: Any Department Checkbox */}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={anyDepartment}
                onChange={async (e) => {
                  const checked = e.target.checked;
                  setAnyDepartment(checked);
                  const visitType =
                    form.visitType === "appointment"
                      ? VISIT_TYPE.APPOINTMENT
                      : VISIT_TYPE.WALK_IN;



                  if (checked) {
                    onChange({ target: { name: "selectedDepartment", value: "" } });
                    onChange({ target: { name: "selectedDoctor", value: "" } });
                    setDoctors([]);
                    setDoctorFee(null);

                    try {
                      setDoctorError(null);
                      setLoadingMessage("Fetching Doctors...");
                      setLoading(true);
                      const res = await getDoctors(visitType, null, form.appointmentDate);
                      setDoctors(res?.data || []);
                    } catch (err) {
                      setDoctors([]);
                      setDoctorError("Failed to load doctors");
                    }
                    finally {
                      setLoading(false);
                    }
                  } else {
                    onChange({ target: { name: "selectedDoctor", value: "" } });
                    setDoctors([]);
                    setDoctorFee(null);

                    if (form.selectedDepartment) {
                      try {
                        setDoctorError(null);
                        setLoadingMessage("Fetching Doctors...");
                        setLoading(true);
                        const res = await getDoctors(visitType, form.selectedDepartment, form.appointmentDate);
                        setDoctors(res?.data || []);
                      } catch (err) {
                        setDoctors([]);
                        setDoctorError("Failed to load doctors");
                      }
                      finally {
                        setLoading(false);
                      }
                    }
                  }
                }}
              />

              <span>{t("doctor.anyDepartment")}</span>
            </label>
          </div>



          <Select styles={reactSelectStyles(isDark, anyDepartment)}
            menuPortalTarget={document.body}
            classNamePrefix="react-select"
            isDisabled={anyDepartment}
            options={departments.map(dept => ({
              value: dept.departmentCode,
              label: dept.departmentName
            }))}
            value={
              form.selectedDepartment
                ? { value: form.selectedDepartment, label: departments.find(d => d.departmentCode === form.selectedDepartment)?.departmentName }
                : null
            }
            onChange={(selected) => {
              onChange({ target: { name: "selectedDepartment", value: selected?.value || "" } });
              // fetch doctors normally if anyDepartment is false
              if (!anyDepartment && selected?.value) {
                try {
                  const visitType =
                    form.visitType === "appointment"
                      ? VISIT_TYPE.APPOINTMENT
                      : VISIT_TYPE.WALK_IN;
                  setLoadingMessage("Fetching Doctors...");
                  setLoading(true);
                  const res = getDoctors(visitType, selected.value, form.appointmentDate).then(res => setDoctors(res?.data || []));
                  setDoctors(res?.data || []);
                } catch (err) {
                  console.error("Error fetching doctors:", err.response || err);
                  setDoctorError("Failed to load doctors");
                  setDoctors([]);
                } finally {
                  setLoading(false);
                }
              }
            }}
            placeholder={loadingDepartments ? "Loading..." : deptError ? deptError : t("doctor.selectDepartment")}
            isClearable
          />
          <p className="text-xs italic text-gray-500 dark:text-gray-400 mt-1">
            {t("bookings.hintDepartment")}
          </p>
        </div>

        {/* ============================
  DOCTOR SELECTION (FULL WIDTH)
=============================== */}
        <div className="w-full mt-4">
          <label className="label text-gray-700 dark:text-gray-200">
            {t("doctor.doctor")} <span className="text-red-500">*</span>
          </label>

          {/* Doctor dropdown */}
          <div className="w-full md:w-1/2">
            <Select styles={reactSelectStyles(isDark)}
              classNamePrefix="react-select"
              options={doctors.map(doc => ({
                value: doc.doctorId,
                label: doctorFee && form.selectedDoctor === doc.doctorId ? `${doc.name} (₹${doctorFee})` : doc.name
              }))}
              value={
                form.selectedDoctor
                  ? { value: form.selectedDoctor, label: doctors.find(d => d.doctorId === form.selectedDoctor)?.name }
                  : null
              }
              onChange={(selected) => {
                handleDoctorChange({ target: { name: "selectedDoctor", value: selected?.value || "" } });
              }}
              required
              placeholder={t("doctor.selectDoctor")}
              isClearable
            />
            <p className="text-xs italic text-gray-500 dark:text-gray-400 mt-1">
              {t("bookings.hintSlot")}
            </p>

            {/* {form.selectedDoctor && doctorFee > 0 && ( */}
            {/* {form.selectedDoctor && (
              <p className="text-xs text-green-600 mt-1">
                ✅{t("bookings.doctorSelected")}
              </p>
            )}

            {form.slotDateTime && (
              <p className="text-xs text-green-600 mt-1">
                ✅{t("bookings.slotConfirmed")}
              </p>
            )} */}


          </div>

          {/* Fee & Slot horizontal container */}
          {/* <div className="flex items-center gap-3 mt-3">

             {doctorFee !== null && doctorFee !== undefined && (
              <div className="px-3 py-1.5 bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200 rounded-md text-xs font-medium flex flex-col items-center">
                <span className="font-semibold text-sm">₹{doctorFee}</span>
                <span className="text-[10px] opacity-80 mt-0.5 text-center w-full">Consultation Fee</span>
              </div>
            )}

           
            {form.slotDateTime && (
              <div className="px-3 py-1.5 bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded-md text-xs font-medium flex flex-col items-center">

                 <span className="text-sm">
                  {form.slotDateTime.startTime} - {form.slotDateTime.endTime}
                </span>

                 {form.slotDateTime.period && (
                  <span className="text-[9px] opacity-90 mt-0.5 text-center flex flex-col items-center w-full">
                     <span className="text-[10px]">{form.slotDateTime.period === "morning" && "🌅"}
                      {form.slotDateTime.period === "afternoon" && "☀️"}
                      {form.slotDateTime.period === "evening" && "🌙"}{form.slotDateTime.period}</span>

                  </span>
                )}
              </div>
            )} 
          </div> */}

        </div>

        {/* ============================
    PAYMENT MODE
=============================== */}
        {(showPayNow || showPayCounter) && (
          <div className="mt-6 w-full md:w-1/2">

            <label className="label text-gray-700 dark:text-gray-200">{t("payment.choosePaymentMode")}</label>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">

              {/* Pay Now Card */}
              {showPayNow && (
                <div
                  onClick={() => !forcePayNow && !isFreeConsultation && handleChange("pay_now")}
                  className={`
                            p-4 rounded-lg border flex flex-col items-start transition shadow-sm
                            ${(forcePayNow || isFreeConsultation) ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:shadow-md"}
                            ${form.paymentMode === "pay_now"
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400"
                      : "border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800"}
                          `}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="font-medium text-gray-700 dark:text-gray-200">
                      💳 {t("payment.payNow")}
                    </span>
                    {form.paymentMode === "pay_now" && (
                      <span className="text-blue-500 dark:text-blue-400 text-lg">✓</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t("doctor.instantOnline")}
                  </p>
                </div>
              )}
              {/* Pay at Counter Card */}
              {showPayCounter && (
                <div
                  onClick={() => !disablePayCounter && !isFreeConsultation && handleChange("pay_counter")}
                  className={`
      p-4 rounded-lg border flex flex-col items-start transition shadow-sm
      ${(disablePayCounter || isFreeConsultation) ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:shadow-md"}
      ${form.paymentMode === "pay_counter"
                      ? "border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-400"
                      : "border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800"}
    `}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="font-medium text-gray-700 dark:text-gray-200">
                      🏥 {t("payment.payAtCounter")}
                    </span>
                    {form.paymentMode === "pay_counter" && (
                      <span className="text-green-500 dark:text-green-400 text-lg">✓</span>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t("doctor.payAtReception")}
                  </p>

                </div>
              )}

            </div>
          </div>
        )}


        <div
          role="button"
          tabIndex={0}
          onClick={() => setShowNotes(v => !v)}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setShowNotes(v => !v)}
          className="
            sm:hidden
            mt-2
            inline-flex items-center gap-2
            px-3 py-1.5
            rounded-full
            text-sm font-medium
            text-gray-600 dark:text-gray-300
            bg-gray-100 dark:bg-gray-700
            hover:bg-gray-200 dark:hover:bg-gray-600
            cursor-pointer
            transition
            select-none
          "
          aria-pressed={showNotes}
          aria-label={showNotes ? t("notes.hide") : t("notes.add")}
        >
          <span className="text-base">📝</span>
          <span>{showNotes ? t("notes.hide") : t("notes.add")}</span>
        </div>


        {/* Notes */}
        <div className={`mt-4 ${showNotes ? "block" : "hidden"} sm:block`}>
          <label className="label text-gray-700 dark:text-gray-200">{t("doctor.notes")}</label>
          <textarea
            rows="3"
            name="specialNotes"
            value={form.specialNotes}
            onChange={onChange}
            className="input resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600
                     focus:ring-2 focus:ring-cyan-400 dark:focus:ring-cyan-500 rounded-md transition-colors"
            placeholder={t("doctor.notesPlaceholder")}
          ></textarea>



        </div>
        <AvailableSlotsPopup
          open={openSlots}
          onClose={setOpenSlots}
          slots={slots} appointmentDate={appointmentDate}
          doctorId={selectedDoctorId}
          onSelectSlot={handleSlotSelect}
        />

        {form.selectedDoctor && (

          <div className="fixed bottom-0 left-0 right-0 pb-[calc(env(safe-area-inset-bottom)+1rem)] bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border-t border-gray-300 dark:border-gray-700 shadow-md px-3 py-3 flex flex-col animate-[slideUp_0.25s_ease-out] border-t border-gray-200/60 dark:border-gray-700/60">
            {form.visitType && (
              <div className="mb-2">
                <div className="flex gap-2">

                  {/* In-person */}
                  <button
                    type="button"
                    onClick={() =>
                      onChange({
                        target: { name: "appointmentType", value: "in_person" }
                      })
                    }
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full
          text-xs font-medium transition
          ${form.appointmentType !== "telemedicine"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}
                  >
                    <FaStethoscope className="text-sm" />
                    {t("consultation.inPerson")}
                  </button>

                  {/* Tele-medicine */}
                  {form.visitType === "appointment" && showTelemedicine && (
                    <button
                      type="button"
                      onClick={() =>
                        onChange({
                          target: { name: "appointmentType", value: "telemedicine" }
                        })
                      }
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full
            text-xs font-medium transition
            ${form.appointmentType === "telemedicine"
                          ? "bg-purple-600 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                        }`}
                    >
                      <FaVideo className="text-sm" />
                      {t("consultation.telemedicine")}
                    </button>
                  )}
                </div>

                {/* Tele-medicine info */}
                {form.visitType === "appointment" &&
                  form.appointmentType === "telemedicine" && (
                    <div
                      className="mt-2 text-[11px] text-purple-700 dark:text-purple-300
            bg-purple-50 dark:bg-purple-900/20
            border border-purple-200 dark:border-purple-700
            rounded-lg px-3 py-2 flex flex-col gap-1.5"
                    >
                      <div className="flex gap-1.5 items-start">
                        <FaVideo className="text-purple-700 dark:text-purple-300 text-lg mr-1" />
                        {t("consultation.telemedicineInfo")}
                      </div>

                      {/* More Info Icon Below */}
                      <div className="flex items-center mt-1 cursor-pointer" onClick={() => setShowTeleInfo(true)}>
                        <FaInfoCircle className="text-purple-700 dark:text-purple-300 text-lg mr-1" />
                        <span className="text-[10px] font-medium text-purple-700 dark:text-purple-300">
                          {t("consultation.moreInfo")}
                        </span>
                      </div>
                    </div>
                  )}

              </div>
            )}



            {slotConflictError && (
              <div className="relative flex items-start justify-between gap-3
    bg-red-50 border border-red-300 text-red-700
    rounded-t-xl px-4 py-2 text-sm shadow-md mb-2">
                <div className="flex gap-2 items-start">
                  <span className="text-red-500 text-lg">⚠</span>
                  <span>{slotConflictError}</span>
                </div>
                <button
                  onClick={() => setSlotConflictError(null)}
                  className="text-red-400 hover:text-red-600 text-lg leading-none px-1"
                >
                  ✕
                </button>
              </div>
            )}

            {/* Top row: Doctor Name + Date/Slot + Pay Button */}
            <div className="flex justify-between items-center gap-2">

              {/* Left: Doctor Name + Date/Slot */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 min-w-0">

                {/* Doctor Name */}
                <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                  Dr. {form.selectedDoctorName || "Select Doctor"}
                </p>

                {/* Date + Slot */}
                {(form.appointmentDate || form.slotDateTime) && (
                  <div className="flex items-center gap-2 mt-1 sm:mt-0 flex-wrap">
                    {form.appointmentDate && (
                      <div
                        className="inline-flex items-center justify-center rounded-full px-2 py-1"
                        style={{
                          backgroundColor: isDarkMode ? "#2563EB" : "#DBEAFE",
                          color: isDarkMode ? "#ffffff" : "#1E40AF",
                          lineHeight: 1,
                        }}
                      >
                        <span className="text-xs font-medium text-gray-800 dark:text-gray-100">
                          <span className="mr-1">📅</span>
                          {new Date(form.appointmentDate).toLocaleDateString('en-GB')}
                        </span>
                      </div>
                    )}
                    {form.slotDateTime && (
                      <div
                        className="inline-flex items-center justify-center rounded-full px-2 py-1"
                        style={{
                          backgroundColor: isDarkMode ? "#F59E0B" : "#FEF3C7",
                          color: isDarkMode ? "#000000" : "#B45309",
                          lineHeight: 1,
                        }}
                      >
                        <span className="text-xs font-medium text-gray-800 dark:text-gray-100">
                          <span className="mr-1">⏰</span>
                          {form.slotDateTime.startTime} - {form.slotDateTime.endTime}
                          {form.slotDateTime.period && <span className="ml-1">({form.slotDateTime.period})</span>}
                        </span>
                      </div>
                    )}
                  </div>
                )}

              </div>

              {/* Pay Button */}
              <div className="flex flex-col items-center">
                <button
                  className={`
                              bg-blue-600 dark:bg-cyan-500 text-white text-sm font-bold  
                              px-3 py-1.5 rounded-lg shadow-md 
                              hover:bg-blue-700 dark:hover:bg-cyan-600 transition-all
                              active:scale-95 active:shadow-inner
                              flex-shrink-0 whitespace-nowrap
                            `}
                >
                  <span className="flex items-center gap-1">
                    <span>₹{doctorFee || 0}</span>
                    <span>Go</span>
                  </span>
                </button>

                {/* subtle hint below button, only if paymentMode is selected */}
                {form.paymentMode && (
                  <span
                    className={`
      mt-0.5 text-[9px] font-medium tracking-wide blink
      ${form.paymentMode === "pay_counter"
                        // ? "text-gray-500 dark:text-gray-400"
                        ? "text-yellow-600 dark:text-yellow-400"
                        : "text-yellow-600 dark:text-yellow-400"}
    `}
                  >
                    {form.paymentMode === "pay_counter" ? "pay at counter" : "pay now"}
                  </span>
                )}


                {/* Free consultation (if any) */}
                {isFreeConsultation && (
                  <span className="mt-0.5 blink text-[9px] text-green-600 dark:text-green-400 font-medium text-center">
                    🎉 Free consultation
                  </span>
                )}
              </div>
            </div>

            {/* 100% secure — only for Pay Now */}
            {form.paymentMode === "pay_now" && (
              <div className="w-full text-center mt-1">
                <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full font-medium">
                  🔒 Secure Payment
                </span>
              </div>
            )}



          </div>
        )}


      </div>
      <TelemedicineInfoPopup
        show={showTeleInfo}
        onClose={() => setShowTeleInfo(false)}
        title={t("consultation.telemedicineInfoTitle")}
        content={t("consultation.telemedicineInfoDetails")}
      />

    </div>
  );
}
